import { useEffect } from 'react';
import { Abbreviation, Book } from '../../../rest/models';
import { useAppDispatch } from '../../../store/hooks';
import { toDict } from '../../../utils';
import { bookEditorActions } from './slice';
import { Button, TextArea } from '../../RequestRespectingInputs';
import { NavLink } from 'react-router-dom';

/**
 * Creates an editor for a book's abbreviations.
 * It displays the abbreviations as a textarea, one per line.
 */
export function BookAbbreviationsEdit(props: Props)
{
    const appDispatch = useAppDispatch();
    const {book, abbrModels} = props;
    const {source_language, abbreviations} = book;

    /**
     * Get the default list for the source language.
     */
    function getAbbrsForSourceLanguage()
    {        
        const abbrs = Object
            .values(abbrModels)
            .filter(a => a.language === source_language)
            .map(a => a.text);

        //Filter duplicates.
        const aDict = toDict(abbrs, a => a, () => true);

        return Object
            .keys(aDict)
            .sort()
            .join('\n')
            .trim();
    }

    function mergeCurrentAndDefault()
    {
        const defaults = toDict(getAbbrsForSourceLanguage().split('\n'), a => a, () => true);
        const current = toDict(abbreviations.split('\n'), a => a, () => true);
        const merged = {...defaults, ...current};

    appDispatch(bookEditorActions.setAbbrs(Object
        .keys(merged)
        .sort()
        .join('\n')
        .trim()));
    }
    
    let justSwitchedLanguages = false;
    useEffect(function()
    {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        justSwitchedLanguages = true;

        //Set abbreviations to either the memoized value or the defaults when the source language changes.
        const memoized = props.abbrsByLang[source_language];
        const defaults = getAbbrsForSourceLanguage();
        appDispatch(bookEditorActions.setAbbrs(memoized ?? defaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source_language]);
    
    useEffect(function()
    {
        if (justSwitchedLanguages)
        {
            //If this is true, the effect above just ran. Don't stomp its work.
            return;
        }

        if (source_language)
        {
            props.abbrsByLang[source_language] = abbreviations;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source_language, abbreviations]);

    return (
        <div className='ernots_BookAbbreviationsEdit'>  
            <p>
                As explained on the <NavLink to='/Abbreviations' target='_blank'>Abbreviations</NavLink> page, abbreviations are words that end with a period, but are not the end of a sentence. "Mr.", "Mrs." etc.
            </p>
            <label htmlFor='abbreviations'>
                Abbreviations
            </label>
            <TextArea
                id='abbreviations'
                value={abbreviations}
                onChange={(e) => appDispatch(bookEditorActions.setAbbrs(e.target.value))}/>
            <br/>
            <Button onClick={mergeCurrentAndDefault}>Merge with default list</Button>
    </div>);
}

interface Props
{
    book: Book;
    abbrModels: {[key: string]: Abbreviation};

    /**
     * If the user switches between languages, this keeps any changes they made to their list,
     * so it can be restored if they return to a previous language.
     * 
     * The data is stored in a memoized value in BookEditForm, since that one doesn't get unmounted while the user is editing.
     */
    abbrsByLang: {[langId: string]: string|undefined};
}